import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import {
  Upload, User, Phone, Mail, Link as LinkIcon,
  CheckCircle, AlertCircle, Loader2, Eye, Type, AlignLeft
} from 'lucide-react';

/* ─── Feed Ad Preview — exact markup from Home.js ──────────────── */
const FeedAdPreview = ({ ad }) => {
  const hasContent = ad.title || ad.content || ad.image_url;

  return (
    <div className="sticky top-6">
      <div className="flex items-center gap-2 mb-3">
        <Eye className="w-4 h-4 text-green-400" />
        <p className="text-green-400 font-mono text-xs uppercase tracking-[0.2em]">
          Live Preview — Feed Card
        </p>
      </div>

      {/* Context: simulated feed with your ad in the middle */}
      <div className="space-y-3">
        {/* Fake post above */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 opacity-30">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-zinc-700" />
            <div className="space-y-1">
              <div className="h-2.5 bg-zinc-700 rounded w-24" />
              <div className="h-2 bg-zinc-800 rounded w-16" />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="h-2.5 bg-zinc-700 rounded w-full" />
            <div className="h-2.5 bg-zinc-700 rounded w-5/6" />
            <div className="h-2.5 bg-zinc-700 rounded w-4/6" />
          </div>
        </div>

        {/* SPONSORED label */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">Sponsored</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>

        {/* The actual ad card — 3-column grid replica (single card shown) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Your ad */}
          <div className="bg-zinc-900 border border-zinc-700 rounded overflow-hidden hover:border-green-500/30 transition-all relative">
            <div className="absolute top-1.5 left-1.5 bg-green-500 text-black text-[8px] font-bold px-1.5 py-0.5 font-mono uppercase tracking-wider">
              YOUR AD
            </div>
            {ad.image_url ? (
              <img
                src={ad.image_url}
                alt={ad.title || 'Ad banner'}
                className="w-full h-24 object-cover bg-black"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-24 bg-zinc-800 flex items-center justify-center">
                <div className="text-center">
                  <Upload className="w-6 h-6 text-zinc-600 mx-auto mb-1" />
                  <p className="text-zinc-600 font-mono text-[10px]">Banner image</p>
                </div>
              </div>
            )}
            <div className="p-3">
              <h4 className="font-display text-sm text-white truncate">
                {ad.title || <span className="text-zinc-600">Your Ad Title</span>}
              </h4>
              <p className="font-mono text-[10px] text-gray-500 truncate">
                {ad.content || <span className="text-zinc-700">Your tagline / description</span>}
              </p>
            </div>
          </div>

          {/* Ghost sibling cards (to show the 3-up grid context) */}
          <div className="hidden sm:block bg-zinc-900/40 border border-zinc-800/50 rounded overflow-hidden opacity-20">
            <div className="w-full h-24 bg-zinc-800/50" />
            <div className="p-3 space-y-1.5">
              <div className="h-2.5 bg-zinc-700 rounded w-3/4" />
              <div className="h-2 bg-zinc-800 rounded w-1/2" />
            </div>
          </div>
          <div className="hidden sm:block bg-zinc-900/40 border border-zinc-800/50 rounded overflow-hidden opacity-20">
            <div className="w-full h-24 bg-zinc-800/50" />
            <div className="p-3 space-y-1.5">
              <div className="h-2.5 bg-zinc-700 rounded w-3/4" />
              <div className="h-2 bg-zinc-800 rounded w-1/2" />
            </div>
          </div>
        </div>

        {/* Fake post below */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 opacity-30">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-zinc-700" />
            <div className="space-y-1">
              <div className="h-2.5 bg-zinc-700 rounded w-20" />
              <div className="h-2 bg-zinc-800 rounded w-14" />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="h-2.5 bg-zinc-700 rounded w-full" />
            <div className="h-2.5 bg-zinc-700 rounded w-3/4" />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-4 bg-zinc-900/50 border border-zinc-800 rounded-sm p-3">
        <p className="text-zinc-500 font-mono text-[10px] leading-relaxed">
          Your ad appears in a 3-column grid between posts. Keep your title short and punchy — it truncates after ~30 characters. The banner image is key; it's the first thing residents see.
        </p>
      </div>

      {!hasContent && (
        <p className="text-zinc-700 font-mono text-xs text-center mt-4">
          Fill in the form to preview your ad →
        </p>
      )}
    </div>
  );
};

/* ─── Main Setup Page ───────────────────────────────────────────── */
const StandardAdSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const sessionId = searchParams.get('session_id');

  const [verifying, setVerifying] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    title: '',
    content: '',
    image_url: '',
    link: '',
    advertiser_name: '',
    phone: '',
    email: '',
  });

  // Verify session on load
  useEffect(() => {
    if (!sessionId) {
      setSessionError('No session ID found. Please return to the Advertise page and complete checkout first.');
      setVerifying(false);
      return;
    }
    api.get(`/billing/verify-standard-session?session_id=${sessionId}`)
      .then(res => {
        const data = res.data;
        setSessionData(data);
        setForm(f => ({
          ...f,
          advertiser_name: data.advertiser_name || '',
          email: data.advertiser_email || '',
        }));
        if (data.already_submitted) {
          setSubmitted(true);
        }
      })
      .catch(err => {
        setSessionError(
          err.response?.data?.detail ||
          'Could not verify your payment. Please contact us at ads@accountability.works.'
        );
      })
      .finally(() => setVerifying(false));
  }, [sessionId]);

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, WEBP, or GIF image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be under 8MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/billing/upload-standard-ad-image?session_id=${sessionId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, image_url: res.data.image_url }));
      toast.success('Banner uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return toast.error('Ad title is required');
    if (!form.content.trim()) return toast.error('A short tagline or description is required');
    if (!form.image_url) return toast.error('Please upload a banner image first');
    if (!form.advertiser_name.trim()) return toast.error('Your business or advertiser name is required');
    setSubmitting(true);
    try {
      await api.post('/billing/submit-standard-ad', {
        session_id: sessionId,
        title: form.title,
        content: form.content,
        image_url: form.image_url,
        link: form.link,
        advertiser_name: form.advertiser_name,
        phone: form.phone,
        email: form.email,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again or email ads@accountability.works.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  // ── Loading state ────────────────────────────────────────────────
  if (verifying) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-green-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-400 font-mono text-sm">Verifying your payment...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Error state ──────────────────────────────────────────────────
  if (sessionError) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-black text-white mb-3">Payment Verification Failed</h2>
            <p className="text-gray-400 mb-6 leading-relaxed">{sessionError}</p>
            <a href="/advertise" className="text-green-400 font-mono text-sm underline">← Return to Advertise Page</a>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Success state ────────────────────────────────────────────────
  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 rounded-full border-2 border-green-500 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-4xl font-black text-white mb-3">Ad Submitted!</h2>
            <p className="text-gray-400 mb-2 leading-relaxed">
              Your standard feed ad is now pending review.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              We'll have it live in the feed within 24 hours.
              {(form.email || sessionData?.advertiser_email) && (
                <> You'll hear from us at <strong className="text-gray-300">{form.email || sessionData?.advertiser_email}</strong>.</>
              )}
            </p>
            <a
              href="/"
              className="inline-block font-mono text-xs text-green-400 border border-green-500/30 px-6 py-3 hover:border-green-500 transition-colors"
            >
              RETURN TO CYQR
            </a>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Main form ────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="min-h-screen bg-[#0D0D0D] text-white">

        {/* Header */}
        <div className="border-b border-green-500/20 bg-[#111]">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-green-400 font-mono text-xs uppercase tracking-widest mb-1">
                <CheckCircle className="w-3.5 h-3.5" />
                <span>Payment confirmed — Standard Feed Ad · $40/month</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-black">Set Up Your Ad</h1>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-gray-600 font-mono text-xs">Preview updates as you type</p>
              <p className="text-gray-700 font-mono text-[10px]">Submit when you're happy with it</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* LEFT: Form */}
            <div className="space-y-6">

              {/* Banner Upload */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6">
                <label className="block font-mono text-xs text-green-400 uppercase tracking-widest mb-4">
                  Banner Image *
                </label>
                <div
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-sm flex flex-col items-center justify-center p-8 cursor-pointer transition-colors ${
                    form.image_url
                      ? 'border-green-500/40 bg-green-500/5'
                      : 'border-[#2a2a2a] hover:border-green-500/30'
                  }`}
                >
                  {form.image_url ? (
                    <div className="text-center w-full">
                      <img
                        src={form.image_url}
                        alt="Banner preview"
                        className="w-full h-24 object-cover rounded mb-3"
                      />
                      <p className="text-green-400 font-mono text-xs">Banner uploaded ✓</p>
                      <p className="text-gray-600 text-xs mt-1">Click to replace</p>
                    </div>
                  ) : uploading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-green-400 animate-spin mx-auto mb-2" />
                      <p className="text-gray-400 font-mono text-xs">Uploading...</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm font-medium">Click to upload your banner</p>
                      <p className="text-gray-700 font-mono text-xs mt-1">JPG, PNG, WEBP or GIF · max 8MB</p>
                      <p className="text-gray-700 font-mono text-[10px] mt-1">Recommended: 600 × 200 px (3:1 ratio)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleBannerUpload}
                />
              </div>

              {/* Ad Copy */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6 space-y-4">
                <label className="block font-mono text-xs text-gray-500 uppercase tracking-widest mb-1">
                  Ad Content
                </label>

                {/* Title */}
                <div>
                  <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">
                    Ad Title * <span className="text-gray-700 normal-case">(shown bold in the card)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-3 text-gray-700"><Type className="w-4 h-4" /></div>
                    <input
                      type="text"
                      value={form.title}
                      onChange={set('title')}
                      placeholder="Acme Plumbing — Regina's Best"
                      maxLength={60}
                      className="w-full pl-10 pr-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-green-500/50 outline-none transition-colors placeholder:text-gray-800"
                    />
                  </div>
                  <p className="text-right font-mono text-[10px] text-gray-700 mt-1">
                    {form.title.length}/60
                  </p>
                </div>

                {/* Content / Tagline */}
                <div>
                  <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">
                    Tagline / Description * <span className="text-gray-700 normal-case">(shown smaller under title)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-3 text-gray-700"><AlignLeft className="w-4 h-4" /></div>
                    <input
                      type="text"
                      value={form.content}
                      onChange={set('content')}
                      placeholder="24/7 service · Licensed & insured · Free estimates"
                      maxLength={120}
                      className="w-full pl-10 pr-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-green-500/50 outline-none transition-colors placeholder:text-gray-800"
                    />
                  </div>
                  <p className="text-right font-mono text-[10px] text-gray-700 mt-1">
                    {form.content.length}/120
                  </p>
                </div>

                {/* Click-through URL */}
                <div>
                  <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">
                    Click-Through URL
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-3 text-gray-700"><LinkIcon className="w-4 h-4" /></div>
                    <input
                      type="text"
                      value={form.link}
                      onChange={set('link')}
                      placeholder="https://yourwebsite.ca"
                      className="w-full pl-10 pr-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-green-500/50 outline-none transition-colors placeholder:text-gray-800"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6 space-y-4">
                <label className="block font-mono text-xs text-gray-500 uppercase tracking-widest mb-1">
                  Advertiser Info{' '}
                  <span className="text-gray-700 normal-case">(for our records — not shown publicly)</span>
                </label>

                {[
                  { key: 'advertiser_name', label: 'Business / Advertiser Name *', placeholder: 'Acme Plumbing Inc.', icon: <User className="w-4 h-4" /> },
                  { key: 'phone',           label: 'Phone',                        placeholder: '(306) 555-0100',       icon: <Phone className="w-4 h-4" /> },
                  { key: 'email',           label: 'Contact Email',                placeholder: 'info@yourbiz.ca',      icon: <Mail className="w-4 h-4" /> },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">
                      {field.label}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-3 text-gray-700">{field.icon}</div>
                      <input
                        type="text"
                        value={form[field.key]}
                        onChange={set(field.key)}
                        placeholder={field.placeholder}
                        className="w-full pl-10 pr-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-green-500/50 outline-none transition-colors placeholder:text-gray-800"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Submit */}
              <div className="bg-[#0D0D0D] border border-green-500/20 p-6">
                <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                  Once submitted, your ad goes to our team for a quick check. We activate it within 24 hours. 
                  Contact us at{' '}
                  <a href="mailto:ads@accountability.works" className="text-green-400 underline">
                    ads@accountability.works
                  </a>{' '}
                  if you need changes after submission.
                </p>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full py-4 font-black uppercase tracking-widest text-sm transition-all disabled:opacity-40 bg-green-500 hover:bg-green-400 text-black"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                    </span>
                  ) : (
                    'Submit My Ad for Review →'
                  )}
                </button>
              </div>
            </div>

            {/* RIGHT: Live Preview */}
            <div>
              <FeedAdPreview ad={form} />
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StandardAdSetup;
